import React, { useState, useMemo } from 'react';
import {
	Stack,
	Heading,
	Icon,
	Box,
	FormControl,
	Textarea,
} from '@chakra-ui/react';
import StepWrapper from '../StepWrapper';
import {
	FaDollarSign,
	FaClipboardList,
	FaStickyNote,
	FaCheckCircle,
	FaCheck,
} from 'react-icons/fa';
import { IoLocationOutline } from 'react-icons/io5';
import { ItemBidModel, ItemModel, UserModel, ItemAskModel, AddressModel } from 'models';
import { Condition } from 'models/types';
import { ItemAskStatus } from 'models/Item/ItemAsk/types';
import { AiFillCheckCircle, AiFillCloseCircle } from 'react-icons/ai';
import Wizard from '../Wizard';
import ItemBidForm, { ItemBidFormData } from '../../Form/ItemBidForm/ItemBidForm';
import ItemBidEditCard from '../../Item/ItemBidEditCard/ItemBidEditCard';
import UnauthorizedAccess from '../../UnauthorizedAccess/UnauthorizedAccess';
import PaymentBreakdown from '../../PaymentBreakdown/PaymentBreakdown';
import ProgressBar from '../../ProgressBar/ProgressBar';
import ConditionDropdown from '../../ConditionDropdown/ConditionDropdown';
import AddressDropdown from '../../AddressDropdown/AddressDropdown';
import { getBidEditPayload } from 'utils/itembid';
import { JSONObject } from 'models/Api/types';

interface ItemBidEditWizardProps {
	item: ItemModel;
	user: UserModel;
	itemBid: ItemBidModel;
	onSubmit?: (
		itemBid: ItemBidModel, 
		editData: JSONObject
	 ) => void;
	isLoading?: boolean;
	hasErrors?: boolean;
	onAddAddressClick?: () => void;
}

export default function ItemBidEditWizard({
	item,
	itemBid,
	user,
	onAddAddressClick,
	onSubmit = () => { },
	isLoading = false,
	hasErrors = false,
}: ItemBidEditWizardProps) {
	const [activeStep, setActiveStep] = useState(0);
	const [editBidData, setEditBidData] = useState<ItemBidFormData>({
		bidPrice: itemBid.amount,
		bidExpiration: itemBid.expirationDays,
	});
	const [condition, setCondition] = useState<Condition>(
		itemBid.condition || Condition.GOOD
	);
	const [notes, setNotes] = useState<string>(itemBid.notes || '');
	const [address, setAddress] = useState<AddressModel>(
		itemBid.address ||
		new AddressModel({
			line1: '',
			line2: '',
			city: '',
			state: '',
			postal_code: '',
			country: '',
		})
	);
	const [useEscrow, setUseEscrow] = useState(itemBid.useEscrow);
	const opposingAskTransaction = useMemo(
		() =>
			new ItemAskModel({
				id: '',
				item_id: item.id,
				user_id: '',
				amount: editBidData?.bidPrice || itemBid.amount,
				currency: 'USD',
				status: ItemAskStatus.PENDING,
				condition: condition,
				notes: notes,
				expiration_days: 30,
				verified: itemBid.isVerified() || false,
				use_escrow: useEscrow,
			}),
		[item.id, editBidData?.bidPrice, condition, notes, itemBid, useEscrow]
	);

	const onWizardSubmit = () => {
		const editPayload = getBidEditPayload(itemBid, editBidData, { condition, notes, address, useEscrow })
		onSubmit(itemBid, editPayload);
	};

	const handleFormChange = (data: ItemBidFormData) => {
		setEditBidData(data);
	};

	const handleConditionChange = (condition: Condition) => {
		setCondition(condition);
	};

	const handleAddressChange = (updated: AddressModel) => {
		setAddress(updated);
	};

	const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setNotes(e.target.value);
	};

	function renderCurrentBidPreview() {
		return (
			<ItemBidEditCard
				item={item}
				originalAmount={itemBid.amount}
				currentAmount={editBidData?.bidPrice}
				createdDaysAgo={itemBid.getCreatedDaysAgo()}
				updatedDaysAgo={itemBid.getUpdatedDaysAgo()}
				expired={itemBid.isExpired()}
				updated={itemBid.hasBeenUpdated()}
				countryCode={address.country || itemBid.getCountryCode()}
				noLongerExpired={(editBidData?.bidExpiration || 0) > 0}
				condition={condition}
			/>
		);
	}

	function renderAmountStep() {
		if (itemBid.belongsToUser(user)) {
			return (
				<Stack spacing={4}>
					{renderCurrentBidPreview()}
					<StepWrapper
						stepKey="amount"
						icon={FaDollarSign}
						title="Bid Amount"
						description="Update your bid price"
						minH="320px"
					>
						<ItemBidForm
							item={item}
							onChange={handleFormChange}
							defaultFormData={editBidData}
							showSubmit={false}
							showItemCard={false}
						/>
					</StepWrapper>
				</Stack>
			);
		} else {
			return (
				<UnauthorizedAccess
					title="Unauthorized"
					description="You are unauthorized to edit this bid."
				/>
			);
		}
	}

	function renderConditionStep() {
		return (
			<Stack spacing={4}>
				{renderCurrentBidPreview()}
				<StepWrapper
					stepKey="condition"
					icon={FaClipboardList}
					title="Acceptable Condition"
					description="Select the minimum condition you will accept"
					minH="320px"
				>
					<ConditionDropdown
						defaultValue={condition}
						onChange={handleConditionChange}
						size="sm"
					/>
				</StepWrapper>
			</Stack>
		);
	}

	function renderNotesStep() {
		return (
			<Stack spacing={4}>
				{renderCurrentBidPreview()}
				<StepWrapper
					stepKey="notes"
					icon={FaStickyNote}
					title="Add Notes"
					description="Additional details for sellers"
					minH="320px"
				>
					<FormControl>
						<Textarea value={notes} onChange={handleNotesChange} placeholder="Enter notes" size="sm" />
					</FormControl>
				</StepWrapper>
			</Stack>
		);
	}

	function renderAddressConfirmStep() {
		const userAddresses = [
			...(user.getPrimaryAddress() ? [user.getPrimaryAddress() as AddressModel] : []),
			...user.address.secondaryAddresses,
		];

		return (
			<Stack spacing={4}>
				{renderCurrentBidPreview()}
				<StepWrapper
					stepKey="addressConfirm"
					icon={IoLocationOutline}
					title="Delivery Address"
					description="Update your delivery address"
					minH="320px"
				>
					<Stack gap={4} alignItems="center" width="100%">
						<AddressDropdown
							addresses={userAddresses}
							defaultAddress={address}
							onSelect={handleAddressChange}
							onAddAddress={onAddAddressClick}
							size="sm"
						/>
					</Stack>
				</StepWrapper>
			</Stack>
		);
	}

	function renderConfirmationStep() {
		return (
			<Stack spacing={4}>
				{renderCurrentBidPreview()}
				<StepWrapper
					stepKey="confirm"
					icon={FaCheckCircle}
					title="Confirm Updated Bid"
					minH="320px"
				>
					<Stack spacing={4}>
						<PaymentBreakdown transaction={opposingAskTransaction} type={'ask'} useEscrow={useEscrow} onToggleEscrow={setUseEscrow} showInfoGuide={false} showTransactionCard={false} />
					</Stack>
				</StepWrapper>
			</Stack>
		);
	}

	function renderWizardFinishingSuccessStep() {
		let content: JSX.Element;
		if (hasErrors) {
			content = (
				<Stack gap={4} alignItems="center">
					<Icon as={AiFillCloseCircle} color="red.500" w={10} h={10} />
					<Heading size="xs" textAlign="center">
						Your Bid Could Not Be Updated.<br />Please Try Again
					</Heading>
				</Stack>
			);
		} else {
			content = (
				<Stack gap={4} alignItems="center">
					<Icon as={AiFillCheckCircle} color="green.500" w={10} h={10} />
					<Heading size="xs" textAlign="center">
						Your Bid Has Been Updated
					</Heading>
				</Stack>
			);
		}

		return (
			<Box height={{ base: 'auto', md: 340 }}>
				{content}
			</Box>
		)
	}

	const wizardSteps = [
		{ label: '', icon: FaDollarSign, content: renderAmountStep(), requiresValidation: true, isValidated: !!editBidData?.bidPrice },
		{ label: '', icon: FaClipboardList, content: renderConditionStep(), requiresValidation: false, isValidated: true },
		{ label: '', icon: FaStickyNote, content: renderNotesStep(), requiresValidation: false, isValidated: true },
		{ label: '', icon: IoLocationOutline, content: renderAddressConfirmStep(), requiresValidation: true, isValidated: !!address.line1},
		{ label: '', icon: FaCheckCircle, content: renderConfirmationStep(), requiresValidation: true, isValidated: true },
	];

	const totalSteps = wizardSteps.length;
	const progressPercentage = ((activeStep + 1) / totalSteps) * 100;
	const progressIcon = activeStep === totalSteps - 1 ? FaCheck : wizardSteps[activeStep].icon;

	return (
		<Stack>
			<ProgressBar progress={progressPercentage} icon={progressIcon} dynamicColor height={4} />
			<Wizard
				steps={wizardSteps}
				finishStep={renderWizardFinishingSuccessStep()}
				loading={isLoading}
				showStepper={false}
				onStepChange={setActiveStep}
				onSubmit={onWizardSubmit}
			/>
		</Stack>
	);
}
